import { useEffect, useMemo } from 'react';
import classNames from 'classnames/bind';
import styles from './Details.module.css';

import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { useSlice } from '../../hooks/useSlice';
import { selectMenu } from '../../features/infoSlice';

import SimpleBar from 'simplebar-react';
import useSimpleBar from '../../hooks/useSimpleBar';

import Button from '../Button';
import NonSelected from './NonSelected';
import Information from './Information';
import Cover from './Cover';
import EditDetail from './EditDetail';
import {
  CLOSE,
  DELETE,
  DELETE_PERMANENTLY,
  DETAILS,
  DETAILS_CLOSE,
} from '../../constants';
import { Options } from '../../types/library';
import useModal from '../../hooks/useModal';
import { TooltipWrapper } from '../Tooltip';

export default function Details() {
  const dispatch = useAppDispatch();
  const cx = classNames.bind(styles);
  const menu = useAppSelector(selectMenu);
  const slice = useSlice();
  const { toggleModal } = useModal();

  const {
    detailsToggled,
    itemSelected,
    selectOptions,
    selectSelectedItem,
    selectSelectedIds,
  } = slice;

  const { sortType, sortOrder, layoutType, isEditMode }: Options =
    useAppSelector(selectOptions);
  const selectedItem = useAppSelector(selectSelectedItem);
  const selectedIds = useAppSelector(selectSelectedIds);
  const [simplebarRef] = useSimpleBar();

  const detailInfoList: any = {
    library: [
      'project',
      'projectCopyright',
      'projectIndex',
      'projectNote',
      'projectResource',
      'projectShare',
      'projectPublic',
      'projectHistoryList',
    ],
    share: [
      'project',
      'projectIndex',
      'projectNote',
      'projectResource',
      'projectShare',
      'projectHistoryList',
    ],
    mydrive: [
      'note',
      'resource',
      selectedItem?.resource?.ext === 'web'
        ? 'url'
        : selectedItem?.resource?.ext === 'folder'
        ? ''
        : 'share',
      'useList',
    ],
    search: ['project', 'projectIndex', 'projectResource', 'projectPublic'],
    trash: ['note', 'resource'],
  }[menu];

  const closeDetails = () => {
    dispatch(detailsToggled());
    !isEditMode && dispatch(itemSelected({ ids: [], item: null }));
  };

  const detailList = useMemo(
    () =>
      isEditMode ? (
        <EditDetail />
      ) : !selectedItem ? (
        <NonSelected />
      ) : (
        <>
          <Cover />
          {detailInfoList.map(
            (name: string) =>
              name && (
                <Information
                  key={name}
                  dataKey={name}
                  subKey={selectedItem?.resource?.ext}
                />
              )
          )}
        </>
      ),
    [selectedItem, isEditMode]
  );

  const deleteProject = () => {
    const id = {
      library: 'confirmDeleteFile',
      share: 'confirmDeleteShared',
      mydrive: 'confirmDeleteFile',
      trash: 'confirmDelCompletly',
    }[menu] as string;
    toggleModal({
      id,
      type: 'confirm',
    });
  };

  useEffect(() => {
    selectedIds &&
      dispatch(itemSelected({ ids: selectedIds, item: selectedItem }));
  }, [selectedIds, selectedItem]);

  return (
    <div
      className={cx('library_info', {
        'is--not-select': isEditMode || !selectedItem,
      })}
    >
      <SimpleBar
        forceVisible={true}
        ref={simplebarRef}
        classNames={{ contentWrapper: 'simplebar-content-wrapper details' }}
      >
        <div className={cx('inner')}>
          <div className={cx('top')}>
            <h4 className={cx('tit')}>{DETAILS}</h4>
            <div className={cx('btn_wrap')}>
              {selectedItem && menu !== 'search' && (
                <TooltipWrapper
                  content={menu === 'trash' ? DELETE_PERMANENTLY : DELETE}
                  onClick={deleteProject}
                >
                  {(tooltipProps) => (
                    <Button
                      name={menu === 'trash' ? DELETE_PERMANENTLY : DELETE}
                      classnames={['book_trash']}
                      isBlindName={true}
                      tooltipProps={tooltipProps}
                    ></Button>
                  )}
                </TooltipWrapper>
              )}
              <TooltipWrapper content={CLOSE} onClick={closeDetails}>
                {(tooltipProps) => (
                  <Button
                    name={DETAILS_CLOSE}
                    classnames={['pop_close']}
                    isBlindName={true}
                    tooltipProps={tooltipProps}
                  >
                    <i className={cx('svg_i')}></i>
                  </Button>
                )}
              </TooltipWrapper>
            </div>
          </div>

          {detailList}
        </div>
      </SimpleBar>
    </div>
  );
}
